import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  noIndex?: boolean;
  language?: "en" | "es";
  jsonLd?: object;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  ogImage = "https://marketbrewer.com/images/og-image/marketbrewer-og.jpg",
  noIndex = false,
  language,
  jsonLd,
}) => {
  const location = useLocation();
  const isSpanish = language === "es" || location.pathname.startsWith("/es");
  const lang = isSpanish ? "es" : "en";
  const siteOrigin = process.env.SITE_ORIGIN || "https://marketbrewer.com";
  const normalizedPath =
    location.pathname === "/" ? "/" : location.pathname.replace(/\/$/, "");
  const canonicalUrl = `${siteOrigin}${normalizedPath}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={isSpanish ? "es_ES" : "en_US"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
